import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    quickLinksGridItem?: GridItemProps;
    ServicesGridItem?: GridItemProps;
    keyWholesaleGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainer: {
        css: css`
            padding-top: 50px;
            margin: auto;
            span[data-test-selector="video"] {
                text-align: center;
                display: block;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 0;
                                padding-left: 18px;
                            `,
                            null,
                            null,
                            css`
                                padding: 1px 18px 0 0;
                            `,
                        ],
                        "min",
                    )}
                a {
                    margin: 0;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 1300px;
                            padding-right: 1rem;
                            padding-left: 1rem;
                        `,
                        css`
                            max-width: 1300px;
                            padding-right: 2rem;
                            padding-left: 2rem;
                        `,
                        css`
                            max-width: 1300px;
                            padding-right: 2rem;
                            padding-left: 2rem;
                        `,
                        css`
                            max-width: 1300px;
                            padding-right: 2rem;
                            padding-left: 2rem;
                        `,
                        css`
                            max-width: 1300px;
                            padding-right: 1rem;
                            padding-left: 1rem;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    logoGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            > div {
                width: 100%;
                .wrapper {
                    max-width: 100%;
                    max-height: 100%;
                }
            }

            img {
                margin-top: 0 !important;
            }

            .list-wrapper {
                .title-wrapper {
                    height: 18px;
                }
            }
        `,
    },
    quickLinksGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            .list-wrapper {
                padding: 0;
            }
            .title-wrapper {
                [class^="TypographyStyle"] {
                    text-transform: uppercase;
                    font-size: 1.5rem;
                    font-weight: 400;
                    margin-bottom: 20px;
                    color: ${getColor("primary.main")};
                }
            }

            .list-link {
                [class^="TypographyStyle"] {
                    text-transform: capitalize;
                    font-size: 1rem;
                    font-weight: 600;
                    margin-bottom: 15px;
                    color: ${getColor("primary.contrast")};
                }
            }
        `,
    },
    ServicesGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            .list-wrapper {
                padding: 0;
            }
            .title-wrapper {
                [class^="TypographyStyle"] {
                    text-transform: uppercase;
                    font-size: 1.5rem;
                    font-weight: 400;
                    margin-bottom: 20px;
                    color: ${getColor("primary.main")};
                }
            }

            .list-link {
                [class^="TypographyStyle"] {
                    text-transform: capitalize;
                    font-size: 1rem;
                    font-weight: 600;
                    margin-bottom: 15px;
                    color: ${getColor("primary.contrast")};
                }
            }
        `,
    },
    keyWholesaleGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            display: flex;
            flex-direction: column;
            .title-wrapper {
                [class^="TypographyStyle"] {
                    text-transform: uppercase;
                    font-size: 1.5rem;
                    font-weight: 400;
                    margin-bottom: 20px;
                    color: ${getColor("primary.main")};
                }
            }
            a[class^="StyledButton-sc"] {
                margin-left: 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            align-items: flex-end;
                        `,
                    ],
                    "min",
                )}
            > div:first-child {
                .wrapper {
                    max-width: 100%;
                }
                margin-bottom: 50px;
            }
            > div {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                text-align: left;
                            `,
                            css`
                                text-align: left;
                            `,
                        ],
                        "min",
                    )}
            }
            .social-links {
                justify-content: center;
                gap: 19px;
                .link-wrapper {
                    margin-right: 0;
                    flex-grow: 0;
                }
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            text-align: left;
                            padding-top: 0px;
                            .list-wrapper {
                                text-align: left;
                            }
                        `,
                        css`
                            text-align: center;
                            padding-top: 100px;
                            .list-wrapper {
                                text-align: right;
                            }
                        `,
                    ],
                    "min",
                )}
            color: ${getColor("common.secondary")};
            font-size: 0.8rem;
            p {
                font-size: 0.8rem;
            }
            .title-wrapper {
                display: none;
            }
            .link-wrapper {
                text-decoration: underline;
                padding: 0 4px;
            }
            .wrapper {
                pre {
                    white-space: pre-wrap;
                    span {
                        font-family: inherit;
                        font-weight: 400;
                        font-size: 0.8rem;
                    }
                }
            }
            a[class^="StyledButton-sc-"] {
                text-decoration: underline;
            }
        `,
    },
    logoWrapper: {
        css: css`
            width: 110px;
            & img {
                width: 90%;
            }
            & .list-wrapper {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                text-align: left;
                            `,
                            css`
                                text-align: center;
                            `,
                        ],
                        "min",
                    )}
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            text-align: center;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    return (
        <GridContainer {...styles.widgetGridContainer}>
            <GridItem {...styles.logoGridItem}>
                <StyledWrapper {...styles.logoWrapper}>
                    <Zone zoneName="Logo" contentId={id} />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.quickLinksGridItem}>
                <Zone zoneName="QuickLinks" contentId={id} />
            </GridItem>
            <GridItem {...styles.ServicesGridItem}>
                <Zone zoneName="Services" contentId={id} />
            </GridItem>
            <GridItem {...styles.keyWholesaleGridItem}>
                <Zone zoneName="KeyWholeSaleLogo" contentId={id} />
                <Zone zoneName="SocialLinks" contentId={id} />
            </GridItem>
            <GridItem {...styles.richContentGridItem}>
                <Zone zoneName="RichContent" contentId={id} />
            </GridItem>
        </GridContainer>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
